import { apiGetProviders } from "@lib/api/provider";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import {
  CoinPriceTextFormat,
  ProviderName,
} from "@lib/components/MiniComponents/Others";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { useTypedSelector } from "@lib/redux";
import {
  AuthRoles,
  CurrencyEnum,
  ProviderTransactionConfig,
  ProviderTransactionConfigType,
} from "@lib/types";
import { UseMutationProps } from "@lib/types/Api";
import { convertNumericFields } from "@lib/types/General";
import { useQuery } from "@tanstack/react-query";
import {
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  mutationQuery: UseMutationProps<Partial<ProviderTransactionConfig>>;
  selected?: ProviderTransactionConfig;
};

export default function PaymentTransactionConfigModal({
  openModal,
  toggleModal,
  mutationQuery,
  selected,
}: Props) {
  const { messages } = useIntl();
  const [form] = Form.useForm();
  const { mutateAsync, isPending } = mutationQuery;
  const { user } = useTypedSelector((state) => state.auth);
  const userRole = user?.role;

  const canSelectProvider = useMemo(() => {
    return userRole === AuthRoles.Admin;
  }, [userRole]);

  const { data: providerData, isLoading: providerIsLoading } = useQuery({
    queryFn: () => apiGetProviders({ limit: 100 }),
    queryKey: ["providers-dropdown"],
    enabled: openModal && !selected && canSelectProvider,
  });

  const [configType, setConfigType] = useState<ProviderTransactionConfigType>(
    selected?.config?.type || "bank_account",
  );
  const provider =
    selected?.provider ??
    providerData?.data?.data?.find((p) => p.id === selected?.providerId);

  const initialValues = useMemo(() => {
    return (
      selected
        ? convertNumericFields(selected)
        : {
            name: "",
            currency: CurrencyEnum.USD,
            from: 0,
            to: 0,
            dailyLimit: 0,
            providerId: "",
            config: {},
          }
    ) as ProviderTransactionConfig;
  }, [selected]);

  useEffect(() => {
    if (typeof form?.setFieldsValue !== "undefined") {
      form?.setFieldsValue(initialValues);
    }
  }, [form, openModal, initialValues]);

  const handleClose = useCallback(() => {
    toggleModal(false);
    form?.resetFields();
  }, [toggleModal, form]);

  return (
    <Modal
      title={
        <FormattedMessage
          id={selected ? "common.editTitle" : "common.createTitle"}
          values={{
            title: messages["common.providerTransactionConfig"] as string,
          }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
      confirmLoading={isPending}
    >
      <Form<ProviderTransactionConfig>
        {...layout}
        form={form}
        name={"provider-transaction-config-modal"}
        onFinish={async (values) => {
          await mutateAsync({
            ...values,
            config: {
              type: configType,
              ...values.config,
            },
            id: selected?.id,
          });
          toggleModal(false);
        }}
        style={{ maxWidth: 600, marginTop: 20 }}
        autoComplete="off"
        initialValues={initialValues}
      >
        <Form.Item
          label={messages["common.name"] as string}
          name="name"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={messages["common.currency"] as string}
          name="currency"
          rules={[{ required: true }]}
        >
          <Select
            options={Object.values(CurrencyEnum).map((currency) => ({
              label: currency,
              value: currency,
            }))}
          />
        </Form.Item>

        <Form.Item
          label={messages["common.from"] as string}
          name="from"
          rules={[{ required: true }]}
        >
          <InputNumber style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          label={messages["common.to"] as string}
          name="to"
          rules={[{ required: true }]}
        >
          <InputNumber style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          label={messages["common.dailyLimit"] as string}
          name="dailyLimit"
          rules={[{ required: true }]}
        >
          <InputNumber style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label={messages["common.dailyTotal"] as string}>
          <CoinPriceTextFormat
            amount={initialValues?.dailyTotal || 0}
            showCurrency={initialValues?.currency || CurrencyEnum.USD}
            showCurrencyImage
            align="left"
          />
        </Form.Item>

        {canSelectProvider && (
          <>
            <Form.Item
              label={messages["common.provider"] as string}
              name="providerId"
              rules={[{ required: true }]}
            >
              {!selected ? (
                <Select
                  options={providerData?.data?.data?.map((provider) => ({
                    label: <ProviderName provider={provider} />,
                    text: provider?.name,
                    value: provider.id,
                  }))}
                  showSearch
                  optionFilterProp="text"
                  loading={providerIsLoading}
                />
              ) : (
                <Input
                  value={provider?.name}
                  disabled
                  addonBefore={<ProviderName provider={provider} />}
                />
              )}
            </Form.Item>
          </>
        )}

        <Divider
          children={
            <>
              <Radio.Group
                value={configType}
                onChange={(e) => setConfigType(e.target.value)}
              >
                <Radio.Button value="bank_account">
                  {messages["common.bankAccount"] as string}
                </Radio.Button>
                <Radio.Button value="card">
                  {messages["common.card"] as string}
                </Radio.Button>
              </Radio.Group>
            </>
          }
        />

        {configType === "card" && (
          <>
            <Form.Item
              label={messages["common.cardNumber"] as string}
              name={["config", "cardNumber"]}
              rules={[{ required: true }]}
            >
              <Input
                style={{ width: "100%" }}
                placeholder="0000 0000 0000 0000"
              />
            </Form.Item>

            <Form.Item
              label={messages["common.cardCvv"] as string}
              name={["config", "cardCvv"]}
              rules={[{ required: true }]}
            >
              <Input style={{ width: "100%" }} placeholder="000" />
            </Form.Item>
            <Form.Item
              label={messages["common.cardExpiry"] as string}
              name={["config", "cardExpiry"]}
              rules={[{ required: true }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="MM/YY"
                minDate={dayjs()}
                allowClear
              />
            </Form.Item>
            <Form.Item
              label={messages["common.cardHolderName"] as string}
              name={["config", "cardHolderName"]}
              rules={[{ required: true }]}
            >
              <Input style={{ width: "100%" }} />
            </Form.Item>
            {/* <Form.Item
              label={messages["common.cardHolderPhone"] as string}
              name="config.cardHolderPhone"
              rules={[{ required: true }]}
            >
              <Input style={{ width: "100%" }} />
            </Form.Item> */}
          </>
        )}

        {configType === "bank_account" && (
          <>
            <Form.Item
              label={messages["common.accountName"] as string}
              name={["config", "accountName"]}
              rules={[{ required: true }]}
            >
              <Input style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              label={messages["common.bankName"] as string}
              name={["config", "bankName"]}
              rules={[{ required: true }]}
            >
              <Input style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              label={messages["common.accountNumber"] as string}
              name={["config", "accountNumber"]}
              rules={[{ required: true }]}
            >
              <Input
                style={{ width: "100%" }}
                placeholder="TRXX XXXX XXXX XXXX XXXX"
              />
            </Form.Item>
          </>
        )}

        <StyledFlex style={{ justifyContent: "end" }}>
          <Space>
            {!selected && (
              <ActionButton
                type="reset"
                onClick={handleClose}
                loading={isPending}
              />
            )}

            <ActionButton
              type={!!selected ? "save" : "add"}
              loading={isPending}
            />
          </Space>
        </StyledFlex>
      </Form>
    </Modal>
  );
}
